import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Send, MessagesSquare, ArrowLeft, BadgeCheck } from "lucide-react";
import { api, resolveFileUrl } from "../api";
import { useAuth } from "../contexts";
import { PageHeader, EmptyState } from "../components/ui-bits";
import { toast } from "sonner";

export default function MessagesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const targetUsername = params.get("to");

  // Bootstrap: open or create a thread when ?to=username is supplied
  useEffect(() => {
    if (!user || !targetUsername) return;
    api.get(`/profile/${targetUsername}`).then((r) => {
      if (r.data?.id) {
        api.post("/messages/threads/start", { recipient_id: r.data.id, body: "" })
          .then((s) => {
            setActiveId(s.data.thread_id);
            params.delete("to");
            setParams(params, { replace: true });
          });
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, [targetUsername, user]);

  const loadThreads = useCallback(() => {
    if (!user) return;
    api.get("/messages/threads").then((r) => setThreads(r.data));
  }, [user]);
  useEffect(() => { loadThreads(); }, [loadThreads]);

  const loadThread = useCallback((tid) => {
    if (!tid) return setThread(null);
    api.get(`/messages/threads/${tid}`).then((r) => {
      setThread(r.data);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      loadThreads();
    });
  }, [loadThreads]);
  useEffect(() => { loadThread(activeId); }, [activeId, loadThread]);

  // Light polling for new messages in the active thread
  useEffect(() => {
    if (!activeId) return;
    const id = setInterval(() => loadThread(activeId), 5000);
    return () => clearInterval(id);
  }, [activeId, loadThread]);

  if (!user) {
    return (
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-16">
        <p className="font-mono-label text-neutral-500 mb-4">Sign in to use messages.</p>
        <Link to="/login" className="btn-primary">Sign In</Link>
      </div>
    );
  }

  const send = async (e) => {
    e.preventDefault();
    const txt = body.trim();
    if (!txt || !activeId) return;
    setBusy(true);
    try {
      await api.post(`/messages/threads/${activeId}`, { body: txt });
      setBody("");
      loadThread(activeId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="pb-32" data-testid="messages-page">
      <PageHeader kicker="DM" title="MESSAGES" sub="Direct lines to the artists, producers, and clients in your network." />
      <div className="divider-tape" />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8 grid md:grid-cols-[320px_1fr] gap-6 min-h-[60vh]">
        {/* THREAD LIST */}
        <aside className={`border border-neutral-800 ${activeId ? "hidden md:block" : ""}`} data-testid="thread-list">
          <div className="p-4 border-b border-neutral-800 font-mono-label text-[10px] text-[var(--accent)] flex items-center gap-2">
            <MessagesSquare size={12} /> THREADS · {threads.length}
          </div>
          {threads.length === 0 ? (
            <div className="p-6 font-mono-label text-[10px] text-neutral-500">
              No threads yet. Visit an artist profile and tap Message to start one.
            </div>
          ) : (
            <ul className="divide-y divide-neutral-800 max-h-[60vh] overflow-y-auto">
              {threads.map((t) => (
                <li key={t.id}>
                  <button
                    onClick={() => setActiveId(t.id)}
                    className={`w-full text-left p-3 hover:bg-neutral-950 transition-colors flex gap-3 ${activeId === t.id ? "bg-neutral-950" : ""}`}
                    data-testid={`thread-${t.id}`}
                  >
                    {t.other?.avatar ? (
                      <img src={resolveFileUrl(t.other.avatar)} alt="" className="w-10 h-10 object-cover border border-neutral-800 shrink-0" />
                    ) : (
                      <div className="w-10 h-10 border border-neutral-800 bg-neutral-900 flex items-center justify-center font-mono-label text-[10px] shrink-0">
                        {(t.other?.display_name || t.other?.username || "?").slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1">
                        <span className="font-headline text-sm uppercase truncate">{t.other?.display_name || t.other?.username || "Unknown"}</span>
                        {t.other?.verified && <BadgeCheck size={11} className="text-[var(--accent)] shrink-0" />}
                      </div>
                      <div className="font-mono-label text-[10px] text-neutral-500 truncate">{t.last_message || "—"}</div>
                    </div>
                    {t.unread > 0 && (
                      <span className="bg-[var(--accent)] text-black font-mono-label text-[10px] px-2 py-0.5 self-center" data-testid={`thread-unread-${t.id}`}>{t.unread}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* THREAD VIEW */}
        <section className={`border border-neutral-800 flex flex-col ${activeId ? "" : "hidden md:flex"}`} data-testid="thread-view">
          {!thread ? (
            <div className="m-auto text-center p-10">
              <MessagesSquare size={28} className="mx-auto text-neutral-600 mb-3" />
              <p className="font-mono-label text-[11px] text-neutral-500">Pick a thread to start chatting.</p>
            </div>
          ) : (
            <>
              <header className="p-4 border-b border-neutral-800 flex items-center gap-3">
                <button onClick={() => setActiveId(null)} className="md:hidden text-neutral-300" data-testid="thread-back"><ArrowLeft size={18} /></button>
                {thread.other?.avatar ? (
                  <img src={resolveFileUrl(thread.other.avatar)} alt="" className="w-10 h-10 object-cover border border-neutral-800" />
                ) : (
                  <div className="w-10 h-10 border border-neutral-800 bg-neutral-900 flex items-center justify-center font-mono-label text-[10px]">
                    {(thread.other?.display_name || thread.other?.username || "?").slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-headline text-sm uppercase truncate flex items-center gap-1">
                    {thread.other?.display_name || thread.other?.username}
                    {thread.other?.verified && <BadgeCheck size={12} className="text-[var(--accent)]" />}
                  </div>
                  <Link to={`/artist/${thread.other?.username}`} className="font-mono-label text-[10px] text-neutral-500 hover:text-[var(--accent)]">
                    @{thread.other?.username}
                  </Link>
                </div>
              </header>

              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="thread-messages">
                {thread.messages.length === 0 ? (
                  <EmptyState>No messages yet — say something.</EmptyState>
                ) : (
                  thread.messages.map((m) => {
                    const mine = m.sender_id === user.id;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                        <div className={`max-w-[78%] px-3 py-2 ${mine ? "bg-[var(--accent)] text-black" : "bg-neutral-900 border border-neutral-800 text-white"}`}>
                          <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
                          <div className={`font-mono-label text-[9px] mt-1 ${mine ? "text-black/70" : "text-neutral-500"}`}>
                            {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={endRef} />
              </div>

              <form onSubmit={send} className="p-3 border-t border-neutral-800 flex gap-2" data-testid="thread-composer">
                <input
                  className="input-brut flex-1"
                  placeholder="Say something…"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  maxLength={2000}
                  data-testid="thread-input"
                />
                <button type="submit" disabled={busy || !body.trim()} className="btn-primary" data-testid="thread-send">
                  <Send size={14} /> Send
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
